import { 
    SEARCH_PRODUCTS_API_PATH,
    PROMOTIONS_PRODUCTS_API_PATH,
    PRODUCT_PRICING_API_PATH,
    PRODUCT_CATEGORIES_BY_BRANCH_API_PATH ,
    PRODUCTS_BY_IDS_API_PATH,
    PRODUCT_DETAILS_BY_ID_API_PATH,
    MANUFACTURERS_BY_BRANCH_API_PATH,
    ORDER_CATALOG_CATEGORIES_API_PATH,
    INVENTORY_LEVELS_API_PATH,
    QUANTITY_CALCULATION_API_PATH,
    PRODUCT_RECOMMENDATIONS_API_PATH,
    CUSTOM_MEASUREMENTS_CALCULATION_API_PATH,
    UNIFIED_QUANTITY_CALCULATION_API_PATH
} from "@/config/constants";
import EpiApiClient, { isCancel } from "@/services/serviceClients/epiApiClient";

const INVENTORY_INFLIGHT = new Map();

const inventoryKey = (payload) => JSON.stringify({
    b: payload?.originBranchCode || '',
    d: payload?.deliveryType,
    i: payload?.specificItemCodes,
    r: payload?.requestDate || null
});

class productCatalogService {
    static async fetchProductCategoriesByBranch(branchCode, orderCatalogFlag) {
        try {
            let API_PATH = '';
            if(orderCatalogFlag===true){
                API_PATH = ORDER_CATALOG_CATEGORIES_API_PATH;
            } else {
                API_PATH = PRODUCT_CATEGORIES_BY_BRANCH_API_PATH + branchCode
            }
            const response = await EpiApiClient.get(API_PATH);
            return {
                success: true,
                data: response.data,
                contentType: response.headers['content-type']
            };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.error_description || "request failed",
                data: {}
            };
        }
    }

    static async fetchManufacturersByBranch(branchCode, top = 10) {
        try {
            const response = await EpiApiClient.get(`${MANUFACTURERS_BY_BRANCH_API_PATH}${branchCode}?top=${top}`);
            return {
                success: true,
                data: response.data,
                contentType: response.headers['content-type']
            };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.error_description || "request failed",
                data: {}
            };
        }
    }

    static async searchProducts(payload, isPromotions = false, cancelToken = null) {
        try {
            const body = { ...payload, splitSelectableFeatures: true };
            const config = cancelToken ? { cancelToken } : {};

            const apiUrl = isPromotions ? PROMOTIONS_PRODUCTS_API_PATH : SEARCH_PRODUCTS_API_PATH;
            const response = await EpiApiClient.post(apiUrl, body, config);

            return {
                success: true,
                data: response.data,
                contentType: response.headers['content-type']
            };
        } catch (error) {
            if (error.__CANCEL__) {
                throw error;
            }
            return {
                success: false,
                error: error.response?.data?.error_description || "request failed",
                data: {}
            };
        }
    }

    static async getProductPricing({
        itemCodes,
        branchCode,
        customerCode,
        shipToSequenceNumber
    }) {
        try {
            const response = await EpiApiClient.post(
                PRODUCT_PRICING_API_PATH,
                {
                    ItemCodes: itemCodes,
                    branchCode,
                    customerCode,
                    shipToSequenceNumber
                }
            );

            return {
                success: true,
                data: response.data,
                contentType: response.headers['content-type']
            };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.error_description || "request failed",
                data: {}
            };
        }
    }

    static async fetchProductsByIds(payload) {
            try {
                const { data } = await EpiApiClient.post(
                    PRODUCTS_BY_IDS_API_PATH,
                    { ...payload, ConcatenateOptions: false }

                );
                return { success: true, data };
            } catch (error) {
                return {
                    success: false,
                    error: error.response?.data?.error_description || "request failed",
                    data: {}
                };
            }
    };

    static async getProductInventoryLevels(payload, cancelToken = null, forceRefresh = false) {
        const key = inventoryKey(payload);

        if (forceRefresh) {
            INVENTORY_INFLIGHT.delete(key);
        }

        const existing = INVENTORY_INFLIGHT.get(key);
        if (existing) return existing;

        const request = (async () => {
            try {
                const response = await EpiApiClient.post(
                    INVENTORY_LEVELS_API_PATH, 
                    payload, 
                    cancelToken ? { cancelToken } : undefined
                );

                return {
                    success: true,
                    data: response.data,
                    contentType: response.headers['content-type']
                };
            } catch (error) {
                if (isCancel(error)) {
                    throw error;
                }
                return {
                    success: false,
                    error: error.response?.data?.error_description || "request failed",
                    data: {}
                };
            } finally {
                INVENTORY_INFLIGHT.delete(key);
            }
        })();

        INVENTORY_INFLIGHT.set(key, request);
        return request;
    }
    
    // Batch product lookup by item codes (used to enrich quote items with image / manufacturer / category / UOM conversion).
    static getProductsByItemCodes(branchCode, itemCodes = [], cancelTokenSource = null) {
        return EpiApiClient.post('/api/services/Catalog/v3/products/byItemCodes',
            {
                branchCode: branchCode,
                itemCodes: itemCodes,
                ConcatenateOptions: false
            },
            {
                headers: { 'Content-Type': 'application/json' },
                handleError: true,
                cancelToken: cancelTokenSource
            })
            .then(function (response) {
                return { success: true, data: response.data.Data };
            })
            .catch(function (error) {
                if (!isCancel(error)) {
                    return {
                        success: false,
                        error: error?.response?.data?.error_description || error.message,
                        data: null
                    };
                }
            });
    }

    static getProductsByIds(branchCode, ids = [], cancelTokenSource = null, excludeVariantsNotAtBranch = false) {
        return EpiApiClient.post(PRODUCTS_BY_IDS_API_PATH,
            {
                branchCode: branchCode,
                productIds: ids,
                ExcludeVariantsNotAtBranch: excludeVariantsNotAtBranch,
                ConcatenateOptions: false
            },
            {
                headers: { 'Content-Type': 'application/json' },
                handleError: true,
                cancelToken: cancelTokenSource
            })
            .then(function (response) {
                return {
                    success: true,
                    data: response.data.Data
                };
            })
            .catch(function (error) {
                if (!isCancel(error)) {
                    return {
                        success: false,
                        error: error && error.response && error.response.data && error.response.data.error_description ? error.response.data.error_description : error.message,
                        data: null
                    };
                }
            });
    }

    static async getQuantityCalculationsV2(payload) {
        try{
            const response = await EpiApiClient.post(QUANTITY_CALCULATION_API_PATH, payload, { handleError: false })
            return { success: true, data: response.data}
        } catch (error) {
            return { success: false, error: error, data: {}}
        }
    }
    
    static async getProductDetailsById(payload, cancelToken = null) {
        try {
            const response = await EpiApiClient.post(PRODUCT_DETAILS_BY_ID_API_PATH, { ...payload, ConcatenateOptions: false }, cancelToken ? { cancelToken } : undefined);

            return {
                success: true,
                data: response.data,
                contentType: response.headers['content-type']
            };
        } catch (error) {
            if (isCancel(error)) {
                throw error;
            }
            return {
                success: false,
                error: error.response?.data?.error_description || "request failed",
                data: {}
            };
        }
    }

    static async getItemUOMConversion(itemCode, cancelTokenSource = null) {
        let url = '/api/services/Catalog/v2/ConvertUOM';

        return EpiApiClient.post(url, { itemCode }, {
            headers: { 'Content-Type': 'application/json' },
            handleError: true,
            cancelToken: cancelTokenSource
        }).then(r => ({ success: true, data: r.data }))
            .catch(e => {
                if (!isCancel(e)) {
                    return {
                        success: false,
                        error: e,
                        data: null
                    };
                }
            });
    }

    static async getQuantityConversion(itemCode, color, fromQty, fromUOM, toUOM) {

        return EpiApiClient.post('/api/services/Catalog/ConvertUOM',
            {
                ItemCode: itemCode,
                Color: color,
                FromQty: fromQty,
                FromUOM: fromUOM,
                ToUOM: toUOM
            })
            .then(function (response) {
                return { success: true, data: response.data };
            }).catch(function (error) {
                return {
                    success: false, error: error.response.data.error_description, data: {}
                };
            });
    }

    static getProductRecommendations(branchCode, categoryId = '', cancelTokenSource = null) {
        let url = `${PRODUCT_RECOMMENDATIONS_API_PATH}/?branchCode=${branchCode}`;
        if( categoryId && categoryId > 0 ) {
            url = `${url}&categoryId=${categoryId}`;
        }

        return EpiApiClient.get(url, {
                headers: { 'Content-Type': 'application/json' },
                handleError: true,
                cancelToken: cancelTokenSource
            })
            .then(function (response) {
                return {
                    success: true,
                    data: response.data.Data
                };
            })
            .catch(function (error) {
                if (!isCancel(error)) {
                    return {
                        success: false,
                        error: error && error.response && error.response.data && error.response.data.error_description ? error.response.data.error_description : error.message,
                        data: null
                    };
                }
            });
    }

    static async calculateCustomMeasurements(payload) {
        try {
        const response = await EpiApiClient.post(
            CUSTOM_MEASUREMENTS_CALCULATION_API_PATH,
            payload
        );

        return {
            success: true,
            data: response.data,
            error: null
        };
        } catch (error) {
        const errorMessage = error.response?.data?.error_description || 
                            error.message || 
                            "Custom measurement calculation failed";

        console.error('CustomMeasurementService.calculateCustomMeasurements failed:', errorMessage, error);

        return {
            success: false,
            data: {},
            error: errorMessage
        };
        }
    }

    /**
     * Unified quantity calculation endpoint
     * Replaces both getQuantityCalculationsV2 and calculateCustomMeasurements for estimates
     * @param {Object} payload - Request payload
     * @param {number} [payload.templateId] - Optional template ID for custom measurement rules
     * @param {Array} payload.measurementCategories - Array of { group, name, value }
     * @param {Array} payload.orderItems - Array of { orderItemIndex, productId, options, uom }
     * @returns {Promise<{success: boolean, data: Array, error: string|null}>}
     */
    static async calculateUnifiedQuantities(payload) {
        try {
            const response = await EpiApiClient.post(
                UNIFIED_QUANTITY_CALCULATION_API_PATH,
                payload
            );
            
            return {
                success: true,
                data: response.data,
                error: null
            };
        } catch (error) {
            const errorMessage = error.response?.data?.error_description || 
                                error.message || 
                                "Quantity calculation failed";
            
            console.error('productCatalogService.calculateUnifiedQuantities failed:', errorMessage, error);
            
            return {
                success: false,
                data: [],
                error: errorMessage
            };
        }
    }
}

// Clear INVENTORY_INFLIGHT cache (e.g. on branch change)
export function clearInventoryInflight() {
    INVENTORY_INFLIGHT.clear();
}

export default productCatalogService;